/**
 * auth.service.ts — SESSION plumbing only.
 *
 * Login, signup, email verification and password reset all live on the
 * centralized SK Platform (platform-fe) now — this app has no auth flow. What
 * remains here is what the product genuinely needs to CONSUME the shared
 * session (httpOnly cookie) and act as a client of its own Billing API:
 *   • fetchAccessToken — cache the platform Bearer token for cross-service calls
 *   • getCurrentUser   — GET /v1/me session probe (5s cache + in-flight dedupe)
 *   • logoutUser       — POST /v1/auth/logout + local teardown
 */
import { platformApi } from '../../api/axiosInstance';
import { TokenHandler } from '../../storage/tokenHandler';
import { Platform_Me_Url, Platform_Logout_Url, Platform_Token_Url } from '../../api/api.endpoints';

/** Fetch (and cache) the platform access token for Bearer use against Billing. */
export const fetchAccessToken = async (): Promise<string | null> => {
  try {
    const { data } = await platformApi.get(Platform_Token_Url);
    // Platform shape is { data: { token } }; tolerate access_token/top-level too.
    const token: string | undefined =
      data?.data?.token ?? data?.data?.access_token ?? data?.access_token ?? data?.token;
    if (token) await TokenHandler.setAccessToken(token);
    return token ?? null;
  } catch {
    return null;
  }
};

/* Me — GET /v1/me (5s cache + in-flight dedupe) */
const ME_TTL = 5_000;
let _meCache: { data: any; at: number } | null = null;
let _meInflight: Promise<any> | null = null;
export const invalidateMeCache = () => {
  _meCache = null;
};
export const getCurrentUser = async (): Promise<any> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = platformApi
      .get(Platform_Me_Url)
      .then((r) => {
        _meCache = { data: r.data, at: Date.now() };
        return r.data;
      })
      .finally(() => {
        _meInflight = null;
      });
  }
  return _meInflight;
};

/* Logout — POST /v1/auth/logout */
export const logoutUser = async (): Promise<void> => {
  try {
    await platformApi.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await TokenHandler.clear();
  }
};
